(() => {
  const SELECTOR = '[data-inspiration-gallery]';
  const instances = new WeakMap();

  const normalize = (value) => (value || 'all').trim().toLowerCase();
  const clamp = (value, min, max, fallback = min) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(Math.max(number, min), max);
  };
  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
  const escapeAttr = (value) => escapeHtml(value).replace(/`/g, '&#096;');
  const sanitizeUrl = (value) => {
    const url = String(value || '').trim();
    if (!url) return '';
    if (/^(https?:)?\/\//i.test(url) || url.startsWith('/')) return url;
    return '';
  };
  const normalizeCategories = (value) => {
    const categories = Array.isArray(value) ? value : String(value || '').split(',');
    return categories
      .map((category) => normalize(category).replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, ''))
      .filter(Boolean);
  };

  const getUrlCategory = () => {
    const params = new URL(window.location.href).searchParams;
    return normalize(params.get('category') || 'all');
  };

  const setUrlCategory = (category, baseUrl) => {
    const url = new URL(baseUrl || window.location.pathname, window.location.origin);

    if (category !== 'all') {
      url.searchParams.set('category', category);
    }

    window.history.pushState({ inspirationCategory: category }, '', `${url.pathname}${url.search}`);
  };

  const getGalleries = (scope = document) => {
    const galleries = [];

    if (scope.matches?.(SELECTOR)) {
      galleries.push(scope);
    }

    scope.querySelectorAll?.(SELECTOR).forEach((root) => galleries.push(root));
    return galleries;
  };

  const initGallery = (root) => {
    if (!root || instances.has(root)) return;

    const filters = Array.from(root.querySelectorAll('[data-inspiration-filter]'));
    let cards = Array.from(root.querySelectorAll('[data-inspiration-card]'));
    const grid = root.querySelector('[data-inspiration-grid]');
    const emptyState = root.querySelector('[data-inspiration-empty]');
    const emptyStateText = emptyState?.textContent || '';
    const baseUrl = root.dataset.baseUrl || window.location.pathname;
    const usesStudioSource = root.dataset.studioSource === 'true';
    const sourceLabel = root.dataset.sourceLabel || '';
    const projectTitleFallback = root.dataset.projectTitleFallback || '';
    const imagePlaceholder = root.dataset.imagePlaceholder || projectTitleFallback;
    const locale = root.dataset.locale || document.documentElement.lang || 'fr';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const transitionDuration = 320;
    let activeCategory = null;
    let filterTimer = null;
    let filterFrame = null;

    const setActiveFilter = (activeCategory) => {
      filters.forEach((filter) => {
        const isCurrent = normalize(filter.dataset.inspirationFilter) === activeCategory;
        if (isCurrent) {
          filter.setAttribute('aria-current', 'page');
        } else {
          filter.removeAttribute('aria-current');
        }
      });
    };

    const updateFilterCounts = () => {
      filters.forEach((filter) => {
        const category = normalize(filter.dataset.inspirationFilter);
        const count = category === 'all'
          ? cards.length
          : getCardState(category).filter(({ isVisible }) => isVisible).length;
        filter.dataset.inspirationCount = String(count);
        const countEl = filter.querySelector('.inspiration-gallery__count');
        if (countEl) countEl.textContent = String(count);
      });
    };

    const getCardState = (category) => {
      const activeCategory = normalize(category);

      return cards.map((card) => {
        const categories = (card.dataset.categories || '')
          .split(',')
          .map((item) => normalize(item))
          .filter(Boolean);
        const isVisible = activeCategory === 'all' || categories.includes(activeCategory);

        return { card, isVisible };
      });
    };

    const updateEmptyState = (visibleCount) => {
      if (emptyState) {
        const waitingForStudio = usesStudioSource
          && cards.length === 0
          && !['ready', 'empty', 'error'].includes(root.dataset.inspirationStudioSourceReady || '');
        emptyState.hidden = waitingForStudio || visibleCount > 0;
      }
    };

    const clearTransitionState = () => {
      window.clearTimeout(filterTimer);

      if (filterFrame) {
        window.cancelAnimationFrame(filterFrame);
        filterFrame = null;
      }

      root.classList.remove('is-filtering');
      cards.forEach((card) => {
        card.classList.remove('is-entering', 'is-hiding', 'is-moving');
        card.style.removeProperty('--inspiration-card-x');
        card.style.removeProperty('--inspiration-card-y');
      });
    };

    const applyCategory = (category, options = {}) => {
      const nextCategory = normalize(category);
      const shouldAnimate =
        options.animate === true && !reduceMotion && activeCategory !== null && activeCategory !== nextCategory;
      const cardStates = getCardState(nextCategory);
      const visibleCount = cardStates.filter(({ isVisible }) => isVisible).length;

      clearTransitionState();
      activeCategory = nextCategory;
      setActiveFilter(nextCategory);

      if (!shouldAnimate) {
        cardStates.forEach(({ card, isVisible }) => {
          card.hidden = !isVisible;
        });
        updateEmptyState(visibleCount);
        return;
      }

      const previousRects = new Map();
      cards.forEach((card) => {
        if (!card.hidden) {
          previousRects.set(card, card.getBoundingClientRect());
        }
      });

      root.classList.add('is-filtering');
      updateEmptyState(visibleCount);

      const enteringCards = [];
      const movingCards = [];

      cardStates.forEach(({ card, isVisible }) => {
        const wasVisible = previousRects.has(card);

        if (isVisible) {
          card.hidden = false;
          if (!wasVisible) {
            card.classList.add('is-entering');
            enteringCards.push(card);
          }
        } else {
          card.hidden = true;
        }
      });

      cardStates.forEach(({ card, isVisible }) => {
        const previousRect = previousRects.get(card);

        if (!isVisible || !previousRect) return;

        const nextRect = card.getBoundingClientRect();
        const deltaX = previousRect.left - nextRect.left;
        const deltaY = previousRect.top - nextRect.top;

        if (Math.abs(deltaX) > 0.5 || Math.abs(deltaY) > 0.5) {
          card.style.setProperty('--inspiration-card-x', `${deltaX}px`);
          card.style.setProperty('--inspiration-card-y', `${deltaY}px`);
          card.classList.add('is-moving');
          movingCards.push(card);
        }
      });

      filterFrame = window.requestAnimationFrame(() => {
        movingCards.forEach((card) => card.classList.remove('is-moving'));
        enteringCards.forEach((card) => card.classList.remove('is-entering'));
        filterFrame = null;
      });

      filterTimer = window.setTimeout(() => {
        [...movingCards, ...enteringCards].forEach((card) => {
          card.classList.remove('is-entering', 'is-moving');
          card.style.removeProperty('--inspiration-card-x');
          card.style.removeProperty('--inspiration-card-y');
        });
        root.classList.remove('is-filtering');
      }, transitionDuration);
    };

    filters.forEach((filter) => {
      filter.addEventListener('click', (event) => {
        const category = normalize(filter.dataset.inspirationFilter);
        event.preventDefault();

        if (category === activeCategory) return;

        setUrlCategory(category, baseUrl);
        applyCategory(category, { animate: true });

        if (!reduceMotion) {
          filter.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    });

    const onPopState = () => applyCategory(getUrlCategory(), { animate: true });

    window.addEventListener('popstate', onPopState);
    applyCategory(getUrlCategory());
    root.dataset.inspirationReady = 'true';

    const resolveApiBase = () => {
      const configuredBase = String(root.dataset.studioApiBase || '').replace(/\/$/, '') || '/apps/todays-studio';
      if (
        configuredBase === '/apps/todays-studio' &&
        ['127.0.0.1', 'localhost'].includes(window.location.hostname)
      ) {
        return 'http://127.0.0.1:8787/apps/todays-studio';
      }
      return configuredBase;
    };

    const normalizeStudioItem = (item = {}, index = 0) => {
      const title = String(item.title || '').trim();
      const subtitle = String(item.subtitle || item.brand || '').trim();
      const image = sanitizeUrl(item.image_url || item.imageUrl || item.image || '');
      return {
        id: String(item.id || `studio-${index + 1}`),
        active: item.active !== false && item.active !== 'false',
        title,
        subtitle,
        sourceUrl: sanitizeUrl(item.source_url || item.sourceUrl || item.project_link || ''),
        sourceRel: normalizeSourceRel(item.source_rel || item.sourceRel || item.link_rel || item.linkRel),
        image,
        imageAlt: String(item.image_alt || item.imageAlt || title || subtitle || '').trim(),
        ratio: ['portrait', 'square', 'landscape', 'tall'].includes(item.image_ratio || item.imageRatio)
          ? String(item.image_ratio || item.imageRatio)
          : 'portrait',
        cropX: clamp(item.crop_x ?? item.cropX, 0, 100, 50),
        cropY: clamp(item.crop_y ?? item.cropY, 0, 100, 50),
        zoom: clamp(item.zoom, 1, 2, 1),
        categories: normalizeCategories(item.category_handles || item.categoryHandles || item.categories),
      };
    };

    const normalizeSourceRel = (value = 'nofollow') => {
      const rel = String(value || 'nofollow').trim().toLowerCase();
      return ['follow', 'nofollow', 'sponsored', 'ugc'].includes(rel) ? rel : 'nofollow';
    };

    const getSourceLinkRel = (sourceRel = 'nofollow') => {
      const rels = new Set(['noopener', 'noreferrer']);
      if (sourceRel === 'sponsored') {
        rels.add('sponsored');
        rels.add('nofollow');
      } else if (sourceRel === 'ugc') {
        rels.add('ugc');
        rels.add('nofollow');
      } else if (sourceRel !== 'follow') {
        rels.add('nofollow');
      }
      return Array.from(rels).join(' ');
    };

    const getStudioCardHtml = (item, index) => {
      const aspectClass = ['portrait', 'square', 'landscape', 'tall'].includes(item.ratio)
        ? `inspiration-gallery__card--${item.ratio}`
        : 'inspiration-gallery__card--portrait';
      const imageHtml = item.image
        ? `<img src="${escapeAttr(item.image)}" alt="${escapeAttr(item.imageAlt || item.title)}" width="900" height="1200" loading="${index < 4 ? 'eager' : 'lazy'}" decoding="async" sizes="(max-width: 479px) calc(100vw - 32px), (max-width: 989px) calc((100vw - 84px) / 2), (max-width: 1559px) calc((100vw - 196px) / 4), 341px">`
        : `<span class="inspiration-gallery__placeholder">${escapeHtml(item.title || imagePlaceholder)}</span>`;
      const sourceHtml = item.sourceUrl
        ? `<a class="inspiration-gallery__source" href="${escapeAttr(item.sourceUrl)}" target="_blank" rel="${escapeAttr(getSourceLinkRel(item.sourceRel))}" aria-label="${escapeAttr(`${sourceLabel} - ${item.title || projectTitleFallback}`)}">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
              <path d="M10.25 13.75a4 4 0 0 0 5.66 0l2.84-2.84a4 4 0 0 0-5.66-5.66l-1.04 1.04" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M13.75 10.25a4 4 0 0 0-5.66 0l-2.84 2.84a4 4 0 0 0 5.66 5.66l1.04-1.04" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>${escapeHtml(sourceLabel)}</span>
          </a>`
        : '';

      return `
        <article class="inspiration-gallery__card ${aspectClass}" data-inspiration-card data-categories="${escapeAttr(item.categories.join(','))}" role="listitem">
          <div class="inspiration-gallery__card-shell">
            <div class="inspiration-gallery__media" style="--inspiration-image-position: ${item.cropX}% ${item.cropY}%; --inspiration-image-scale: ${item.zoom};">
              ${imageHtml}
            </div>
            <div class="inspiration-gallery__card-footer">
              <div class="inspiration-gallery__caption">
                ${item.title ? `<h2 class="inspiration-gallery__project-title">${escapeHtml(item.title)}</h2>` : ''}
                ${item.subtitle ? `<p class="inspiration-gallery__brand">${escapeHtml(item.subtitle)}</p>` : ''}
              </div>
              ${sourceHtml}
            </div>
          </div>
        </article>
      `;
    };

    const loadStudioItems = async () => {
      if (!usesStudioSource || !grid) return;
      root.dataset.inspirationStudioSourceReady = 'loading';
      try {
        const studioChannel = root.dataset.studioChannel === 'draft' ? 'draft' : 'live';
        const inspirationUrl = new URL(`${resolveApiBase()}/inspiration`, window.location.origin);
        inspirationUrl.searchParams.set('channel', studioChannel);
        inspirationUrl.searchParams.set('locale', window.TodayMerchPublicApi?.normalizeLocale(locale) || locale);
        const response = await fetch(inspirationUrl.toString(), {
          headers: { Accept: 'application/json' },
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) {
          root.dataset.inspirationStudioSourceReady = 'error';
          const payload = await response.json().catch(() => ({}));
          if (emptyState) {
            emptyState.textContent = window.TodayMerchPublicApi?.errorMessage(payload, locale) || emptyStateText;
          }
          updateEmptyState(0);
          return;
        }
        const payload = await response.json();
        const items = (payload.items || [])
          .map(normalizeStudioItem)
          .filter((item) => item.active && (item.title || item.subtitle || item.image));
        if (!items.length) {
          root.dataset.inspirationStudioSourceReady = 'empty';
          if (emptyState) emptyState.textContent = emptyStateText;
          updateFilterCounts();
          applyCategory(getUrlCategory());
          return;
        }
        grid.innerHTML = items.map(getStudioCardHtml).join('');
        cards = Array.from(root.querySelectorAll('[data-inspiration-card]'));
        updateFilterCounts();
        applyCategory(getUrlCategory());
        root.dataset.inspirationStudioSourceReady = 'ready';
      } catch (error) {
        root.dataset.inspirationStudioSourceReady = 'error';
        if (emptyState) {
          emptyState.textContent = window.TodayMerchPublicApi?.errorMessage(
            { code: 'INSPIRATION_REQUEST_FAILED' },
            locale
          ) || emptyStateText;
        }
        updateEmptyState(0);
      }
    };

    loadStudioItems();

    instances.set(root, () => {
      clearTransitionState();
      window.removeEventListener('popstate', onPopState);
      root.removeAttribute('data-inspiration-ready');
    });
  };

  const initAll = (scope = document) => {
    getGalleries(scope).forEach(initGallery);
  };

  const destroyAll = (scope = document) => {
    getGalleries(scope).forEach((root) => {
      const destroy = instances.get(root);
      if (destroy) {
        destroy();
        instances.delete(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
